<template>
  <section class="chart-asset-quarter">
    <companyTitle :isShowCompanyInfo="true"></companyTitle>
    <error-alert v-if="$store.getters.errorMessage" />
    <template v-else>
      <b-row class="justify-content-around">
        <ChartLine
          id="asset-analyse-quarter"
          :config="itemsOfAssetAnalyseQuarter"
        />
        <ChartLine
          id="total-liabilities-quarter"
          :config="itemsOfTotalLiabilitiesQuarter"
        />
        <ChartLine
          id="current-assets-quarter"
          :config="itemsOfCurrentAssetsQuarter"
        />
        <ChartLine
          id="current-liabilities-quarter"
          :config="itemsOfCurrentLiabilitiesQuarter"
        />
        <ChartLine id="liquidity-quarter" :config="itemsOfLiquidityQuarter" />
        <ChartLine
          id="working-apital-quarter"
          :config="itemsOfWorkingCapitalQuarter"
        />
      </b-row>
    </template>
  </section>
</template>

<script>
import ChartLine from "@/components/ChartLine.vue";
import ErrorAlert from "@/components/ErrorAlert.vue";
import companyTitle from "@/components/CompanyTitle.vue";

export default {
  name: "ChartAssetQuarter",
  components: {
    ChartLine,
    ErrorAlert,
    companyTitle
  },
  computed: {
    /***********************************************************
      資產總額分析，欄位如下： 
      current_assets ( 流動資產 )
      long_term_investments ( 長期投資 )
      property_plant_and_equipment ( 不動產廠房及設備 )
      non_current_assets ( 非流動資產 )
    ***********************************************************/
    itemsOfAssetAnalyseQuarter() {
      const chartFields = [
        {
          key: "current_assets",
          color: this.$store.getters.chartColors.eucalyptus
        },
        {
          key: "long_term_investments",
          color: this.$store.getters.chartColors.dodgerBlue
        },
        {
          key: "property_plant_and_equipment",
          color: this.$store.getters.chartColors.fuchsiaBlue
        },
        {
          key: "non_current_assets",
          color: this.$store.getters.chartColors.pumpkin
        }
      ];
      const chartOptions = {
        title: "資產總額分析",
        step: 10,
        //此圖 y 軸單位是百分比
        axesYUnit: "percentage"
      };
      const chartData = this.$store.getters.formatToChartDataQuarter(
        this.$store.getters.chartAssetQuarter.assetAnalyse,
        chartFields,
        chartOptions
      );
      return chartData;
    },

    /***********************************************************
      負債總額分析，欄位如下： 
      current_liabilities ( 流動負債 )
      corporate_bond ( 公司債 )
      bank_loan_non_current ( 銀行借款－非流動 )
      other_long_term_debt_payable_non_current ( 其他長期借款－非流動 )
      non_current_liabilities ( 非流動負債 )
    ***********************************************************/
    itemsOfTotalLiabilitiesQuarter() {
      const chartFields = [
        {
          key: "current_liabilities",
          color: this.$store.getters.chartColors.amber
        },
        {
          key: "corporate_bond",
          color: this.$store.getters.chartColors.dodgerBlue
        },
        {
          key: "bank_loan_non_current",
          color: this.$store.getters.chartColors.amaranth
        },
        {
          key: "other_long_term_debt_payable_non_current",
          color: this.$store.getters.chartColors.pumpkin
        },
        {
          key: "non_current_liabilities",
          color: this.$store.getters.chartColors.eucalyptus
        }
      ];
      const chartOptions = {
        title: "負債總額分析",
        step: 10,
        //此圖 y 軸單位是百分比
        axesYUnit: "percentage"
      };
      const chartData = this.$store.getters.formatToChartDataQuarter(
        this.$store.getters.chartAssetQuarter.totalLiabilities,
        chartFields,
        chartOptions
      );
      return chartData;
    },

    /***********************************************************
      流動資產，欄位如下： 
      cash_and_cash_equivalents ( 現金及約當現金 )
      short_term_investment_financial_assets_current ( 短期投資金融資產－流動 )
      accounts_and_bills_receivable ( 應收帳款及票據 )
      other_receivables ( 其他長期借款－非流動 )
      loan_to_other_S_T ( 短期借支 )
      inventories ( 存貨 )
      prepaid_expenses_and_repayments ( 預付費用及預付款 )
      other_current_assets ( 其他流動資產 )
    ***********************************************************/
    itemsOfCurrentAssetsQuarter() {
      const chartFields = [
        {
          key: "cash_and_cash_equivalents",
          color: this.$store.getters.chartColors.dodgerBlue
        },
        {
          key: "short_term_investment_financial_assets_current",
          color: this.$store.getters.chartColors.fuchsiaBlue
        },
        {
          key: "accounts_and_bills_receivable",
          color: this.$store.getters.chartColors.eucalyptus
        },
        {
          key: "other_receivables",
          color: this.$store.getters.chartColors.amaranth
        },
        {
          key: "loan_to_other_S_T",
          color: this.$store.getters.chartColors.pumpkin
        },
        {
          key: "inventories",
          color: this.$store.getters.chartColors.pelorous
        },
        {
          key: "prepaid_expenses_and_repayments",
          color: this.$store.getters.chartColors.amber
        },
        {
          key: "other_current_assets",
          color: this.$store.getters.chartColors.cerise
        }
      ];
      const chartOptions = {
        title: "流動資產",
        step: 10,
        //此圖 y 軸單位是百分比
        axesYUnit: "percentage"
      };
      const chartData = this.$store.getters.formatToChartDataQuarter(
        this.$store.getters.chartAssetQuarter.currentAssets,
        chartFields,
        chartOptions
      );
      return chartData;
    },

    /***********************************************************
      流動負債，欄位如下： 
      short_term_debt ( 短期借款 )
      commercial_paper_payable_acceptance_bill ( 應付商業本票 ∕ 承兌匯票 )
      short_term_financial_liability_current ( 短期金融負債－流動 )
      accounts_and_bills_payable ( 應付帳款及票據 )
      other_payables ( 其他應付款 )
      current_tax_liabilities ( 當期所得稅負債 )
      other_financial_liabilities_current ( 其他金融負債－流動 )
      long_term_liabilities_due_within_one_year ( 一年內到期長期負債 )
      other_current_liabilities ( 非流動負債 )
    ***********************************************************/
    itemsOfCurrentLiabilitiesQuarter() {
      const chartFields = [
        {
          key: "short_term_debt",
          color: this.$store.getters.chartColors.cerise
        },
        {
          key: "commercial_paper_payable_acceptance_bill",
          color: this.$store.getters.chartColors.fuchsiaBlue
        },
        {
          key: "short_term_financial_liability_current",
          color: this.$store.getters.chartColors.mountainMeadow
        },
        {
          key: "accounts_and_bills_payable",
          color: this.$store.getters.chartColors.dodgerBlue
        },
        {
          key: "other_payables",
          color: this.$store.getters.chartColors.amber
        },
        {
          key: "current_tax_liabilities",
          color: this.$store.getters.chartColors.eucalyptus
        },
        {
          key: "other_financial_liabilities_current",
          color: this.$store.getters.chartColors.pelorous
        },
        {
          key: "long_term_liabilities_due_within_one_year",
          color: this.$store.getters.chartColors.amaranth
        },
        {
          key: "other_current_liabilities",
          color: this.$store.getters.chartColors.pumpkin
        }
      ];
      const chartOptions = {
        title: "流動負債",
        step: 10,
        //此圖 y 軸單位是百分比
        axesYUnit: "percentage"
      };
      const chartData = this.$store.getters.formatToChartDataQuarter(
        this.$store.getters.chartAssetQuarter.currentLiabilities,
        chartFields,
        chartOptions
      );
      return chartData;
    },

    /***********************************************************
      償債能力分析，欄位如下： 
      cash_and_cash_equivalents ( 現金及約當現金 )
      short_term_debt ( 短期借款 )
      long_term_liabilities_due_within_one_year ( 一年內到期長期負債 )
      corporate_bond ( 公司債 )
      bank_loan_non_current ( 銀行借款－非流動 )
      EBIDTA ( 稅前息前折舊前淨利－EBIDTA )
    ***********************************************************/
    itemsOfLiquidityQuarter() {
      const chartFields = [
        {
          key: "cash_and_cash_equivalents",
          color: this.$store.getters.chartColors.eucalyptus
        },
        {
          key: "short_term_debt",
          color: this.$store.getters.chartColors.pelorous
        },
        {
          key: "long_term_liabilities_due_within_one_year",
          color: this.$store.getters.chartColors.pumpkin
        },
        {
          key: "corporate_bond",
          color: this.$store.getters.chartColors.amaranth
        },
        {
          key: "bank_loan_non_current",
          color: this.$store.getters.chartColors.fuchsiaBlue
        },
        {
          key: "EBIDTA",
          color: this.$store.getters.chartColors.dodgerBlue
        }
      ];
      const chartOptions = {
        title: "償債能力分析",
        step: null,
        //此圖 y 軸單位是元
        axesYUnit: "price"
      };
      const chartData = this.$store.getters.formatToChartDataQuarter(
        this.$store.getters.chartAssetQuarter.liquidity,
        chartFields,
        chartOptions
      );
      return chartData;
    },

    /***********************************************************
      營運資金週期，欄位如下： 
      days_of_cash_receivable ( 應收帳款收現天數 )
      days_of_sales ( 銷貨天數 )
      days_of_accounts_payable_turnover ( 應付帳款週轉天數 )
      days_of_operation_turnover ( 營運週轉天數 )
      days_of_cash_turnover ( 現金週轉天數 )
    ***********************************************************/
    itemsOfWorkingCapitalQuarter() {
      const chartFields = [
        {
          key: "days_of_cash_receivable",
          color: this.$store.getters.chartColors.dodgerBlue
        },
        {
          key: "days_of_sales",
          color: this.$store.getters.chartColors.amaranth
        },
        {
          key: "days_of_accounts_payable_turnover",
          color: this.$store.getters.chartColors.fuchsiaBlue
        },
        {
          key: "days_of_operation_turnover",
          color: this.$store.getters.chartColors.pumpkin
        },
        {
          key: "days_of_cash_turnover",
          color: this.$store.getters.chartColors.eucalyptus
        }
      ];
      const chartOptions = {
        title: "營運資金週期",
        step: null,
        //此圖 y 軸單位是數字
        axesYUnit: "number"
      };
      const chartData = this.$store.getters.formatToChartDataQuarter(
        this.$store.getters.chartAssetQuarter.workingCapital,
        chartFields,
        chartOptions
      );
      return chartData;
    }
  }
};
</script>

<style lang="scss" scoped></style>
